import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import ClearIcon from '@mui/icons-material/Clear';

import { Box, Button, IconButton } from '@mui/material';

import { ALL_SETTLEMENTS_FILTER_VALUE, DATE_FILTER_VALUES, FILTER_DATE_OPTIONS } from './constants';
import { useStyles } from './styles';

import FormAutocompleteFieldServerSearch from '../formFields/FormAutocompleteFieldServerSearch';
// import FormToggleField from '../formFields/FormToggleField';
import FormSelectField from '../formFields/FormSelectField';
import { useIsFirstRender } from '../../hooks/useIsFirstRender';
import { FilterTypes, SelectOption } from '../../types';
import {
  handleGetCaseTypes,
  handleGetDefendants,
  handleGetPlaintiffs,
  handleGetVendors,
  mapCaseTypesQueryResults,
  mapDefendantsQueryResults,
  mapPlaintiffsQueryResults,
  mapVendorsQueryResults,
} from '../../views/CasesView/helpers';
import useWindowWidth from '../../hooks/useWindowWidth';
import usePagination from '../../hooks/usePagination';
import FormDateField from '../formFields/FormDateField';
import moment from 'moment';

interface ITableFilters {
  filters: FilterTypes;
  setFilters: React.Dispatch<React.SetStateAction<FilterTypes>>;
  setMatch?: (arg: string) => void;
  searchDebounce?: number;
  timeFilter?: string;
  typeFilter?: boolean;
  statusFilter?: SelectOption[];
  // Initial value of the Status filter; "Clear filters" empties it to list records of every status
  defaultStatus?: string;
  client?: boolean;
  defendant?: boolean;
  rolesFilter?: SelectOption[];
  match?: string;
  caseType?: boolean;
  dateRange?: boolean;
  transactionsStatusFilter?: boolean;
  typeFilterOptions?: SelectOption[];
  isStatusArray?: boolean;
  isPlaintiff?: boolean;
  accountType?: SelectOption[];
  transactionType?: SelectOption[];
  vendorType?: SelectOption[];
  defaultPaymentMethod?: SelectOption[];
  verificationStatus?: SelectOption[];
  vendorStatus?: SelectOption[];
  vendor?: boolean;
  mappedDeductionStatus?: SelectOption[];
  onboardingStatus?: SelectOption[];
  approvalStatusFilter?: SelectOption[];
  settlementTypeFilter?: SelectOption[];
  ledgerSettlementFilter?: SelectOption[];
  bankingTransactionDefaultFilter?: Partial<FilterTypes>;
}

const TableFilters = ({
  setFilters,
  timeFilter,
  typeFilter,
  statusFilter,
  defaultStatus,
  client,
  defendant,
  rolesFilter,
  setMatch,
  match,
  caseType,
  dateRange,
  typeFilterOptions,
  isStatusArray,
  isPlaintiff,
  accountType,
  transactionType,
  vendorType,
  defaultPaymentMethod,
  verificationStatus,
  vendorStatus,
  vendor,
  mappedDeductionStatus,
  onboardingStatus,
  approvalStatusFilter,
  settlementTypeFilter,
  ledgerSettlementFilter,
  bankingTransactionDefaultFilter,
}: ITableFilters) => {
  const styles = useStyles();
  const isFirstRender = useIsFirstRender();
  const windowWidth = useWindowWidth();
  const { setPageNumber } = usePagination();

  const showUserOnboardingDetails = localStorage.getItem('showUserOnboardingDetails') === 'true';
  const showApprovalFlow = localStorage.getItem('hasSettlementApprovalFlow') === 'true';

  const firmId = localStorage.getItem('firmId');

  const [, setDateFilter] = useState<string>('');

  const formMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      searchQuery: '',
      isArchived: false,
      clientId: { id: '', label: '' },
      defendantId: { id: '', label: '' },
      type: '',
      dateFilter: '',
      status: defaultStatus ?? '',
      role: '',
      roleId: '',
      caseTypeId: { id: '', label: '' },
      state: '',
      from: '',
      to: '',
      statuses: [],
      plaintiffId: { id: '', label: '' },
      accountType: 'Revenue Recognition',
      transactionType: 'All Types',
      vendorType: 'All Vendor Types',
      defaultPaymentMethod: 'All Methods',
      vendorStatus: 'All Statuses',
      verificationStatus: 'All Statuses',
      vendorId: { id: '', label: '' },
      mappedDeductionStatus: '',
      onboardingStatus: 'All Statuses',
      processingStatus: 'All Statuses',
      settlementType: 'All Types',
      settlementId: ALL_SETTLEMENTS_FILTER_VALUE,
    },
  });

  const {
    watch,
    reset,
    formState: { isDirty, defaultValues },
  } = formMethods;

  const statusFieldValue = watch('status');
  // A default Status (e.g. Active users) is an applied filter even though the form is not dirty
  const hasDefaultStatusApplied = !!defaultStatus && !!statusFieldValue;

  const clientFieldValue = watch('clientId');
  const plaintiffFieldValue = watch('plaintiffId');
  const defendantFieldValue = watch('defendantId');
  const roleFieldValue = watch('role');
  const caseTypeFieldValue = watch('caseTypeId');
  const dateFromFieldValue = watch('from');
  const dateToFieldValue = watch('to');
  const vendorFieldValue = watch('vendorId');

  useEffect(() => {
    if (isFirstRender) return;

    setFilters((prevState) => {
      const newState = { ...prevState };

      // Autocomplete fields: set when a value is selected, delete when cleared
      if (clientFieldValue?.id) {
        newState.clientId = clientFieldValue.id;
      } else if ('clientId' in prevState) {
        delete newState.clientId;
      }

      if (plaintiffFieldValue?.id) {
        newState.plaintiffId = plaintiffFieldValue.id;
      } else if ('plaintiffId' in prevState) {
        delete newState.plaintiffId;
      }

      if (defendantFieldValue?.id) {
        newState.defendantId = defendantFieldValue.id;
      } else if ('defendantId' in prevState) {
        delete newState.defendantId;
      }

      if (caseTypeFieldValue?.id) {
        newState.caseTypeId = caseTypeFieldValue.id;
      } else if ('caseTypeId' in prevState) {
        delete newState.caseTypeId;
      }

      if (vendorFieldValue?.id) {
        newState.vendorId = vendorFieldValue.id;
      } else if ('vendorId' in prevState) {
        delete newState.vendorId;
      }

      // Scalar fields
      if (roleFieldValue) {
        newState.role = roleFieldValue;
      } else if ('role' in prevState) {
        delete newState.role;
      }

      if (dateFromFieldValue) {
        newState.from = dateFromFieldValue;
      } else if ('from' in prevState) {
        delete newState.from;
      }

      if (dateToFieldValue) {
        newState.to = dateToFieldValue;
      } else if ('to' in prevState) {
        delete newState.to;
      }

      if (JSON.stringify(prevState) !== JSON.stringify(newState)) {
        setPageNumber && setPageNumber(1);
        return newState;
      }

      return prevState;
    });
  }, [
    clientFieldValue,
    defendantFieldValue,
    roleFieldValue,
    caseTypeFieldValue,
    dateFromFieldValue,
    dateToFieldValue,
    plaintiffFieldValue,
    vendorFieldValue,
  ]);

  const customHandleDateChange = (value: string) => {
    setPageNumber && setPageNumber(1);
    setDateFilter(value);

    const fromDate = new Date(DATE_FILTER_VALUES[value as keyof typeof DATE_FILTER_VALUES].from);
    const toDate = new Date(DATE_FILTER_VALUES[value as keyof typeof DATE_FILTER_VALUES].to);

    setFilters((prevState) => ({
      ...prevState,
      date: { from: fromDate, to: toDate },
    }));
  };

  const handleFiltersGroupClick = (value: string | string[], name: string) => {
    setPageNumber && setPageNumber(1);

    setFilters((prevState) => ({
      ...prevState,
      [name]: name === 'onboardingStatus' ? value : isStatusArray ? [value] : value,
    }));
  };

  const statusFilterField = statusFilter ? (
    <FormSelectField
      label='Status'
      options={statusFilter}
      name={isStatusArray ? 'statuses' : 'status'}
      minWidth='180px'
      handleChange={handleFiltersGroupClick}
    />
  ) : null;

  const handleResetFilters = () => {
    setFilters({
      firmId: firmId || '',
      ...bankingTransactionDefaultFilter,
    });
    setMatch && setMatch('');
    // Clearing empties the default Status too, so records of every status are listed
    reset(defaultStatus ? { ...defaultValues, status: '' } : undefined);
    setDateFilter('');
  };

  return (
    <FormProvider {...formMethods}>
      <form className={styles.classes.filtersForm}>
        <Box className={styles.classes.filtersContainer}>
          {dateRange ? (
            <Box display='flex' gap='10px'>
              <FormDateField
                label='From'
                name='from'
                maxDate={dateToFieldValue ? moment(dateToFieldValue) : moment()}
                maxWidth='180px'
                readonly
              />

              <FormDateField
                label='To'
                name='to'
                maxDate={moment()}
                minDate={moment(dateFromFieldValue)}
                maxWidth='180px'
                readonly
              />
            </Box>
          ) : null}

          {/* With a Roles filter (users list), Status is shown after it instead */}
          {!rolesFilter && statusFilterField}

          {client ? (
            <FormAutocompleteFieldServerSearch
              name={isPlaintiff ? 'plaintiffId' : 'clientId'}
              label='Client'
              optionsQuery={handleGetPlaintiffs}
              mapQueryResults={mapPlaintiffsQueryResults}
              filters={{ firmId: firmId || undefined }}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              minWidth='180px'
              fetchSize={100}
              minSearchChars={3}
            />
          ) : null}

          {defendant ? (
            <FormAutocompleteFieldServerSearch
              name='defendantId'
              label='Defendant'
              optionsQuery={handleGetDefendants}
              mapQueryResults={mapDefendantsQueryResults}
              filters={{ firmId: firmId || undefined }}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              minWidth='180px'
              fetchSize={100}
              minSearchChars={3}
            />
          ) : null}

          {caseType
            ? firmId && (
              <FormAutocompleteFieldServerSearch
                name='caseTypeId'
                label='Case Type'
                optionsQuery={handleGetCaseTypes}
                mapQueryResults={mapCaseTypesQueryResults}
                filters={{ firmId }}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                minWidth='180px'
                fetchSize={100}
                minSearchChars={3}
              />
            )
            : null}

          {timeFilter ? (
            <FormSelectField
              name='dateFilter'
              label={timeFilter}
              options={FILTER_DATE_OPTIONS}
              handleChange={customHandleDateChange}
              minWidth='180px'
            />
          ) : null}

          {rolesFilter ? (
            <>
            <FormSelectField
              label='Roles'
              options={rolesFilter}
              name='roleId'
              handleChange={handleFiltersGroupClick}
              minWidth='180px'
            />
              {statusFilterField}
            </>
          ) : null}

          {typeFilter ? (
            <FormSelectField
              name='type'
              label='Type'
              options={typeFilterOptions as SelectOption[]}
              minWidth='180px'
              handleChange={handleFiltersGroupClick}
            />
          ) : null}

          {accountType ? (
            <FormSelectField
              label='ACCOUNT TYPE'
              name='accountType'
              options={accountType}
              minWidth='180px'
              handleChange={handleFiltersGroupClick}
            />
          ) : null}

          {transactionType ? (
            <FormSelectField
              label='TRANSACTION TYPE'
              options={transactionType as SelectOption[]}
              name='transactionType'
              minWidth='180px'
              handleChange={handleFiltersGroupClick}
            />
          ) : null}

          {vendorType ? (
            <FormSelectField
              label='VENDOR TYPE'
              name='vendorType'
              options={vendorType}
              minWidth='180px'
              handleChange={handleFiltersGroupClick}
            />
          ) : null}

          {defaultPaymentMethod ? (
            <FormSelectField
              label='METHODS'
              options={defaultPaymentMethod as SelectOption[]}
              name='defaultPaymentMethod'
              minWidth='180px'
              handleChange={handleFiltersGroupClick}
            />
          ) : null}

          {vendorStatus ? (
            <FormSelectField
              label='VENDOR ACCOUNT STATUS'
              name='vendorStatus'
              options={vendorStatus as SelectOption[]}
              minWidth='180px'
              handleChange={handleFiltersGroupClick}
            />
          ) : null}

          {verificationStatus ? (
            <FormSelectField
              label='VERIFICATION STATUS'
              options={verificationStatus as SelectOption[]}
              name='verificationStatus'
              minWidth='180px'
              handleChange={handleFiltersGroupClick}
            />
          ) : null}
          
          {vendor ? (
            <FormAutocompleteFieldServerSearch
              name='vendorId'
              label='Vendor'
              optionsQuery={handleGetVendors}
              mapQueryResults={mapVendorsQueryResults}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              minWidth='180px'
              fetchSize={100}
              minSearchChars={3}
            />
          ) : null}

          {mappedDeductionStatus ? (
            <FormSelectField
              label='Deduction Status'
              options={mappedDeductionStatus}
              name='mappedDeductionStatus'
              minWidth='180px'
              handleChange={handleFiltersGroupClick}
            />
          ) : null}

          {showApprovalFlow && approvalStatusFilter ? (
            <FormSelectField
              label='Approval Status'
              options={approvalStatusFilter}
              name='processingStatus'
              minWidth='180px'
              handleChange={handleFiltersGroupClick}
            />
          ) : null}

          {(showUserOnboardingDetails && onboardingStatus) ? (
            <FormSelectField
              label='Onboarding Status'
              options={onboardingStatus}
              name='onboardingStatus'
              minWidth='180px'
              handleChange={handleFiltersGroupClick}
            />
          ) : null}

          {settlementTypeFilter ? (
            <FormSelectField
              label='Settlement Type'
              options={settlementTypeFilter}
              name='settlementType'
              minWidth='180px'
              handleChange={handleFiltersGroupClick}
            />
          ) : null}

          {ledgerSettlementFilter ? (
            <FormSelectField
              label='Settlement'
              options={ledgerSettlementFilter}
              name='settlementId'
              minWidth='180px'
              handleChange={handleFiltersGroupClick}
            />
          ) : null}

          {windowWidth <= 550 ? (
            <Button
              disableRipple
              className={styles.classes.clearFiltersButton}
              disabled={!(isDirty || match?.length || hasDefaultStatusApplied)}
              onClick={handleResetFilters}
            >
              Clear filters
            </Button>
          ) : (
            <IconButton
              disableRipple
              className={styles.classes.clearIconContainer}
              disabled={!(isDirty || match?.length || hasDefaultStatusApplied)}
              onClick={handleResetFilters}
            >
              <ClearIcon />
            </IconButton>
          )}
        </Box>
      </form>
    </FormProvider>
  );
};

export default TableFilters;